import { useState, useEffect, useRef } from "react";
import { Lock, Sun, ShieldCheck, FileText } from "lucide-react";

const CORRECT_PASSWORD = "Victura12@";

export default function VicturaDataroom() {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const containerRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setUnlocked(true);
      setError("");
    } else {
      setError("Incorrect password. Please check with your EnergyX representative.");
    }
  };

  useEffect(() => {
    if (unlocked && containerRef.current) {
      fetch("/victura-hr-oa.html")
        .then((res) => res.text())
        .then((html) => {
          const iframe = document.createElement("iframe");
          iframe.style.cssText = "width:100%;height:100vh;border:none;";
          iframe.srcdoc = html;
          containerRef.current.innerHTML = "";
          containerRef.current.appendChild(iframe);
        });
    }
  }, [unlocked]);

  if (unlocked) {
    return <div ref={containerRef} style={{ width: "100%", height: "100vh" }} />;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(165deg, #0f2444 0%, #1F3864 40%, #2a4a7a 100%)" }}>
      {/* Subtle grid pattern overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Top bar */}
      <div className="relative z-10" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <span className="text-lg font-black tracking-tighter text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Energy<span style={{ color: "#00D68F" }}>X</span>
          </span>
          <div className="flex items-center gap-2 text-white/40 text-xs">
            <ShieldCheck size={14} />
            <span>Secure Dataroom</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          {/* Header block */}
          <div className="text-center mb-10">
            <div
              className="w-20 h-20 mx-auto flex items-center justify-center mb-8"
              style={{
                background: "linear-gradient(135deg, rgba(232,163,61,0.15) 0%, rgba(232,163,61,0.05) 100%)",
                border: "1px solid rgba(232,163,61,0.25)",
              }}
            >
              <Sun size={36} color="#E8A33D" />
            </div>

            <p
              className="text-xs font-bold tracking-[0.2em] uppercase mb-4"
              style={{ color: "#E8A33D" }}
            >
              Confidential Dataroom
            </p>
            <h1
              className="text-3xl md:text-4xl font-black text-white tracking-tight leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Victura Group
            </h1>
            <h2
              className="text-lg md:text-xl font-bold mt-2 tracking-tight"
              style={{ color: "rgba(255,255,255,0.7)", fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Haryana Open Access Solar
            </h2>

            <p className="text-white/40 text-sm mt-5 max-w-sm mx-auto leading-relaxed" style={{ fontFamily: "'Satoshi', sans-serif" }}>
              This dataroom contains the portfolio overview, financial projections, scenario analysis, and term sheet for the Victura captive open access solar project.
            </p>
          </div>

          {/* Info chips */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {[
              { icon: FileText, text: "Portfolio & Savings" },
              { icon: Sun, text: "12,000 kWp DC" },
              { icon: ShieldCheck, text: "Tosham Solar Park, HR" },
            ].map((chip, i) => {
              const Icon = chip.icon;
              return (
                <div
                  key={i}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-medium"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.5)",
                  }}
                >
                  <Icon size={13} style={{ color: "#E8A33D" }} />
                  {chip.text}
                </div>
              );
            })}
          </div>

          {/* Password card */}
          <div
            className="p-8"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Lock size={16} style={{ color: "#E8A33D" }} />
              <span className="text-white/70 text-sm font-bold">Enter your access password</span>
            </div>

            {error && (
              <div
                className="text-sm font-medium p-3 mb-5 flex items-center gap-2"
                style={{ background: "rgba(197,90,17,0.12)", color: "#E8A33D", border: "1px solid rgba(232,163,61,0.15)" }}
                data-testid="dataroom-error"
              >
                <ShieldCheck size={14} />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="dataroom-form">
              <input
                type="password"
                placeholder="Dataroom password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                data-testid="dataroom-password"
                className="w-full h-12 px-4 text-sm text-white placeholder:text-white/25 focus:outline-none transition-all"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
                onFocus={(e) => e.target.style.borderColor = "rgba(232,163,61,0.4)"}
                onBlur={(e) => e.target.style.borderColor = "rgba(255,255,255,0.12)"}
              />
              <button
                type="submit"
                data-testid="dataroom-submit"
                className="w-full h-12 text-sm font-bold transition-all hover:-translate-y-0.5 hover:shadow-lg"
                style={{ background: "#E8A33D", color: "#1F3864" }}
              >
                Access Dataroom
              </button>
            </form>
          </div>

          {/* Footer note */}
          <p className="text-center text-white/20 text-xs mt-8" style={{ fontFamily: "'Satoshi', sans-serif" }}>
            Prepared by EnergyX (APT EnergyX Solar Pvt Ltd) &middot; Confidential
          </p>
        </div>
      </div>
    </div>
  );
}
